// src/pages/LavorazioniAssegnate.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { authFetch } from '../utils/authFetch';
import { useNavigate } from 'react-router-dom';
import { Card, TableCell, TableHeadCell } from '../components/ui';

const API_BASE = '/api';

// helper per avere una chiave yyyy-mm-dd
function formatDateKey(date) {
  if (!date) return '';
  const d = new Date(date);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const weekdayLabels = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('it-IT');
};

const formatInternalCode = (workOrder, iterationOverride) => {
  if (!workOrder) return '-';

  const base =
    workOrder.internalCode ||
    workOrder.codiceInterno ||
    (workOrder.workOrder && workOrder.workOrder.internalCode) ||
    '-';

  const iter =
    typeof iterationOverride === 'number'
      ? iterationOverride
      : typeof workOrder.iteration === 'number'
      ? workOrder.iteration
      : typeof workOrder.workOrder?.iteration === 'number'
      ? workOrder.workOrder.iteration
      : 0;

  return iter > 0 ? `${base}-${iter}` : base;
};

const getStatusInfo = (workOrder) => {
  if (!workOrder) {
    return {
      label: '-',
      className: 'bg-gray-100 text-gray-700 border border-gray-300',
    };
  }

  const status = workOrder.status || 'in_corso';
  const iter =
    typeof workOrder.iteration === 'number' ? workOrder.iteration : 0;

  if (status === 'chiusa') {
    return {
      label: 'Terminata',
      className: 'bg-gray-100 text-gray-700 border border-gray-300',
    };
  }

  if (status === 'sospesa') {
    return {
      label: 'Sospesa',
      className: 'bg-amber-100 text-amber-800 border border-amber-200',
    };
  }

  if (
    workOrder.assignedTo?.nome === 'Da' &&
    workOrder.assignedTo?.cognome === 'Assegnare'
  ) {
    return {
      label: 'Aperta',
      className: 'bg-orange-100 text-orange-800 border border-orange-200',
    };
  }

  if (status === 'in_corso') {
    if (iter > 0) {
      return {
        label: 'Riassegnata',
        className:
          'bg-purple-100 text-purple-800 border border-purple-200',
      };
    }
    return {
      label: 'Attiva',
      className:
        'bg-emerald-100 text-emerald-800 border border-emerald-200',
    };
  }

  return {
    label: status,
    className: 'bg-gray-100 text-gray-700 border border-gray-300',
  };
};

const LavorazioniAssegnate = () => {
  const navigate = useNavigate();
  const role = localStorage.getItem('role') || '';

  // mese visualizzato
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const [events, setEvents] = useState([]);
  const [selectedDateKey, setSelectedDateKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const handleBack = () => {
    if (role === 'tecnico') {
      navigate('/dashboard');
      return;
    }

    navigate('/lavorazioni');
  };

  const monthLabel = useMemo(
    () =>
      currentMonth.toLocaleDateString('it-IT', {
        month: 'long',
        year: 'numeric',
      }),
    [currentMonth]
  );

  // matrice giorni mese corrente (stesso approccio di CalendarioAziendale)
  const daysMatrix = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const firstWeekday = (firstDay.getDay() + 6) % 7; // lun=0

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const weeks = [];
    let currentWeek = new Array(firstWeekday).fill(null);

    for (let day = 1; day <= daysInMonth; day++) {
      currentWeek.push(new Date(year, month, day));
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) currentWeek.push(null);
      weeks.push(currentWeek);
    }
    return weeks;
  }, [currentMonth]);

  // mappa date -> eventi assegnati in quel giorno
  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((ev) => {
      const key = formatDateKey(ev.date);
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    });
    return map;
  }, [events]);

  const selectedEvents = useMemo(() => {
    if (!selectedDateKey) return [];
    return eventsByDate[selectedDateKey] || [];
  }, [eventsByDate, selectedDateKey]);

  // cambio mese
  const changeMonth = (delta) => {
    setCurrentMonth((prev) => {
      const next = new Date(prev);
      next.setMonth(prev.getMonth() + delta);
      return new Date(next.getFullYear(), next.getMonth(), 1);
    });
  };

  // carica le lavorazioni assegnate all'utente per il mese corrente
  const loadMyEvents = async () => {
    setLoading(true);
    setError('');
    setInfo('');

    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const monthStart = new Date(year, month, 1);
      const monthEnd = new Date(year, month + 1, 0);

      const from = formatDateKey(monthStart);
      const to = formatDateKey(monthEnd);

      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to) params.append('to', to);

      const res = await authFetch(
        `${API_BASE}/work/my-events?${params.toString()}`
      );

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.error ||
            'Errore nel recupero delle lavorazioni assegnate.'
        );
      }

      const data = await res.json();
      setEvents(data || []);

      if (!data || data.length === 0) {
        setInfo('Nessuna lavorazione assegnata per questo mese.');
      } else {
        setInfo('');
      }
    } catch (err) {
      console.error('Errore caricamento mie lavorazioni:', err);
      setError(err.message || 'Errore nella richiesta al server.');
    } finally {
      setLoading(false);
    }
  };

  // carica ogni volta che cambia mese
  useEffect(() => {
    loadMyEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth]);

  // quando arrivano eventi, se non c'è una data selezionata, seleziona il primo giorno con eventi
  useEffect(() => {
    if (events.length === 0) {
      setSelectedDateKey('');
      return;
    }
    if (!selectedDateKey) {
      const first = events[0];
      setSelectedDateKey(formatDateKey(first.date));
    }
  }, [events, selectedDateKey]);

  const formatSelectedDateLabel = () => {
    if (!selectedDateKey) return '';
    const d = new Date(selectedDateKey + 'T00:00:00');
    return d.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };



  return (
    <div className="min-h-screen bg-gray-100 pb-10">
      <div className="max-w-6xl mx-auto px-4 pt-8">
        {/* HEADER */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-1">
              Lavorazioni assegnate
            </h1>
            <p className="text-sm text-gray-600">
              Visualizza sul calendario tutte le lavorazioni assegnate al tuo
              account.
            </p>
          </div>
            <button
              onClick={handleBack}
              className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200"
            >
              ← Indietro
            </button>
        </div>

        {loading ? (
          <Card className="shadow-sm p-6 text-sm text-gray-600">
            Caricamento calendario personale...
          </Card>
        ) : (
          <div className="space-y-5">
            {/* CALENDARIO PERSONALE */}
            <Card className="shadow-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <button
                  type="button"
                  onClick={() => changeMonth(-1)}
                  className="px-2 py-1 rounded-lg text-sm text-gray-600 hover:bg-gray-100"
                >
                  &larr; Mese prec.
                </button>
                <span className="font-semibold text-gray-800 capitalize">
                  {monthLabel}
                </span>
                <button
                  type="button"
                  onClick={() => changeMonth(1)}
                  className="px-2 py-1 rounded-lg text-sm text-gray-600 hover:bg-gray-100"
                >
                  Mese succ. &rarr;
                </button>
              </div>

              {/* intestazione giorni settimana */}
              <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-gray-500 mb-1">
                {weekdayLabels.map((d) => (
                  <div key={d} className="py-1">
                    {d}
                  </div>
                ))}
              </div>

              {/* griglia giorni */}
              <div className="space-y-1">
                {daysMatrix.map((week, wi) => (
                  <div key={wi} className="grid grid-cols-7 gap-1">
                    {week.map((date, di) => {
                      if (!date) return <div key={di} className="h-12 md:h-14" />;

                      const key = formatDateKey(date);
                      const dayEvents = eventsByDate[key] || [];
                      const hasEvents = dayEvents.length > 0;
                      const isSelected = key === selectedDateKey;

                      let classes =
                        'h-12 md:h-14 rounded-lg border text-xs flex flex-col items-center justify-center cursor-pointer transition ';
                      if (isSelected && hasEvents) {
                        classes +=
                          'bg-blue-600 border-blue-700 text-white font-semibold';
                      } else if (hasEvents) {
                        classes +=
                          'bg-blue-50 border-blue-200 text-blue-700 font-medium hover:bg-blue-100';
                      } else {
                        classes +=
                          'bg-white border-gray-200 text-gray-800 hover:bg-gray-50';
                      }

                      return (
                        <button
                          key={di}
                          type="button"
                          className={classes}
                          onClick={() => setSelectedDateKey(key)}
                        >
                          <span>{date.getDate()}</span>
                          {hasEvents && (
                            <span className="text-[9px] mt-0.5">
                              {dayEvents.length} lav.
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </Card>

            {/* PANNELLO DETTAGLIO GIORNO SELEZIONATO */}
            <Card className="shadow-sm p-4 md:p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg md:text-xl font-semibold text-gray-800">
                  Dettaglio lavorazioni
                </h2>
                {selectedDateKey && (
                  <span className="text-xs md:text-sm text-gray-600 capitalize">
                    {formatSelectedDateLabel()}
                  </span>
                )}
              </div>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">
                  {error}
                </p>
              )}
              {info && !selectedEvents.length && (
                <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 mb-3">
                  {info}
                </p>
              )}

              {selectedEvents.length === 0 ? (
                <p className="text-sm text-gray-500">
                  Nessuna lavorazione selezionata per questo giorno.
                </p>
              ) : (
                <>
                  <p className="text-[11px] text-gray-400 mb-2 md:hidden">
                    Scorri orizzontalmente la tabella per visualizzare tutti i dati.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse text-xs md:text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-gray-700">
                          <TableHeadCell className="px-3 py-2 text-left">
                            Stato
                          </TableHeadCell>
                          <TableHeadCell className="px-3 py-2 text-left">
                            Cod. interno
                          </TableHeadCell>
                          <TableHeadCell className="px-3 py-2 text-left">
                            Tipo
                          </TableHeadCell>
                          <TableHeadCell className="px-3 py-2 text-left">
                            Preventivo
                          </TableHeadCell>
                          <TableHeadCell className="px-3 py-2 text-left">
                            Azienda
                          </TableHeadCell>
                          <TableHeadCell className="px-3 py-2 text-left">
                            Luogo
                          </TableHeadCell>
                          <TableHeadCell className="px-3 py-2 text-left">
                            Tipo apparecchiatura
                          </TableHeadCell>
                          <TableHeadCell className="px-3 py-2 text-left">
                            Produttore
                          </TableHeadCell>
                          <TableHeadCell className="px-3 py-2 text-left">
                            Modello
                          </TableHeadCell>
                          <TableHeadCell className="px-3 py-2 text-left">
                            Seriale
                          </TableHeadCell>
                          <TableHeadCell className="px-3 py-2 text-left">
                            Inventario
                          </TableHeadCell>
                          <TableHeadCell className="px-3 py-2 text-left">
                            Presidio
                          </TableHeadCell>
                          <TableHeadCell className="px-3 py-2 text-left">
                            Data ricezione
                          </TableHeadCell>
                          <TableHeadCell className="px-3 py-2 text-left">
                            Note Generali
                          </TableHeadCell>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedEvents.map((ev) => {
                          const o = ev.workOrder || {};
                          const statusInfo = getStatusInfo(o);
                          const preventivoLabel =
                            o.type === 'intervento'
                              ? o.preventivoStatus || '-'
                              : '-';

                          return (
                            <tr
                              key={ev._id}
                              className="border-b last:border-b-0 hover:bg-gray-50"
                            >
                              <TableCell className="px-3 py-2 align-top">
                                <span
                                  className={`inline-flex px-2 py-1 rounded-full text-[10px] font-semibold ${statusInfo.className}`}
                                >
                                  {statusInfo.label}
                                </span>
                              </TableCell>
                              <TableCell className="px-3 py-2 align-top whitespace-nowrap">
                                {formatInternalCode(o, ev.iteration)}
                              </TableCell>
                              <TableCell className="px-3 py-2 align-top capitalize">
                                {o.type || '-'}
                              </TableCell>
                              <TableCell className="px-3 py-2 align-top whitespace-nowrap">
                                {preventivoLabel}
                              </TableCell>
                              <TableCell className="px-3 py-2 align-top">
                                {o.companyName || '-'}
                              </TableCell>
                              <TableCell className="px-3 py-2 align-top">
                                {o.place || '-'}
                              </TableCell>
                              <TableCell className="px-3 py-2 align-top">
                                {o.equipmentType || '-'}
                              </TableCell>
                              <TableCell className="px-3 py-2 align-top">
                                {o.equipmentMaker || '-'}
                              </TableCell>
                              <TableCell className="px-3 py-2 align-top">
                                {o.equipmentModel || '-'}
                              </TableCell>
                              <TableCell className="px-3 py-2 align-top whitespace-nowrap">
                                {o.equipmentSerial || '-'}
                              </TableCell>
                              <TableCell className="px-3 py-2 align-top whitespace-nowrap">
                                {o.equipmentInventory || '-'}
                              </TableCell>
                              <TableCell className="px-3 py-2 align-top">
                                {o.equipmentPresidio || '-'}
                              </TableCell>
                              <TableCell className="px-3 py-2 align-top whitespace-nowrap">
                                {formatDate(o.receivedAt)}
                              </TableCell>
                              <TableCell className="px-3 py-2 align-top">
                                {o.description || '-'}
                              </TableCell>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </Card>
          </div>
        )}

        {error && !loading && (
          <p className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </p>
        )}
      </div>
    </div>
  );
};

export default LavorazioniAssegnate;
